import {createCapabilityUdoColumnResolver} from "~/utilities/capabilityUdoTemplateColumns";
import {createFileRoute} from "@tanstack/react-router";
import {
    fetchAnalyticsData,
    getDatasets,
} from "~/routes/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId/ottopilot/_ottopilotScaffold/dashboard/-api";
import {ErrorComponent} from "./-errorComponent";
import {NotFoundComponent} from "./-notFoundComponent";
import {PendingComponent} from "./-pendingComponent";
import {RouteComponent} from "./-routeComponent";
import type {User} from "@vesta/vos-types/modules/workspaceLevel/users/domain/User";
import {EMPTY_CACHEABLE_WORKSPACE_ITEM_COMPLEX_FILTER, getWorkspaceUsers} from "~/utilities/queries";
import {getDhiraaguDashboardConfig} from "~/routes/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId/ottopilot/_ottopilotScaffold/dashboard/-dashboardConfig/dhiraaguDashboardConfig";
import {getDefaultOneMonthDateRangeIsoBounds} from "~/utilities/defaultDateRange";
import {isDhiraaguWorkspace} from "~/domainModels/workspaceSpecific/dhiraagu";
import {getEmotoradDashboardConfig} from "~/routes/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId/ottopilot/_ottopilotScaffold/dashboard/-dashboardConfig/emotoradDashboardConfig";
import {EmotoradRoles, isEmotoradWorkspace} from "~/domainModels/workspaceSpecific/emotorad";
import type {DashboardConfig} from "./-types";
import {AmaronRoles, isAmaronWorkspace} from "~/domainModels/workspaceSpecific/amaron";
import {getAmaronDashboardConfig} from "~/routes/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId/ottopilot/_ottopilotScaffold/dashboard/-dashboardConfig/amaronDashboardConfig";
import {DASHBOARD_DATASET_KEYS_BY_WORKSPACE} from "~/routes/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId/ottopilot/_ottopilotScaffold/dashboard/-constants";
import {resolveDashboardDatasetsAndColumns} from "~/routes/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId/ottopilot/_ottopilotScaffold/dashboard/-helpers";
import {
    DatasetColumnRole,
    DatasetType,
    FilterType,
    MetricAggregationFunction,
} from "@vesta/vos-types/modules/workspaceLevel/domain/Dataset";
import {AnalyticsQueryVariant} from "@vesta/vos-types/modules/workspaceLevel/domain/RawDataType";
import {getRequiredEventContainerTemplate} from "~/contexts/workspaceEventContainerTemplatesContext";
import {AmaronEventContainerTypes} from "@vesta/vos-types/modules/workspaceLevel/workspaceSpecific/domain/amaron/EventContainers";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {getEventContainerDispositionOptions} from "~/utilities/ottopilotSemanticColumns";

const {start: TS_START, end: TS_END} = getDefaultOneMonthDateRangeIsoBounds();

export const Route = createFileRoute(
    "/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId/ottopilot/_ottopilotScaffold/dashboard",
)({
    loader: async ({context}) => {
        const workspace = context.workspace;
        const workspaceCapabilities = context.workspaceCapabilities;
        const timeZone = workspace.timeZoneId;
        const intellsysBaseUrl = context.intellsysBaseUrl;
        const accessToken = context.authenticationStatus.accessToken;
        const user = context.currentWorkspaceUser;
        const workspaceId = context.workspace.id;
        const currentPlatformUser = context.profileStatus.platformUser;
        const platformUsers = context.platformUserDetails;

        const userTemplates = context.userTemplates;
        const ouTemplates = context.organizationalUnitTemplates;
        const usersResult = await getWorkspaceUsers({
            workspaceId: workspaceId,
            offset: 0,
            limit: 9999,
            complexFilters: EMPTY_CACHEABLE_WORKSPACE_ITEM_COMPLEX_FILTER,
            orderBy: [],
            intellsysBaseUrl: context.intellsysBaseUrl,
            accessToken: context.authenticationStatus.accessToken,
        });
        if (usersResult.success !== true) {
            throw usersResult.error;
        }
        const users: Array<User> = usersResult.data;

        const platformUserDetailsByUserId = new Map(platformUsers.map((platformUser) => [platformUser.userId, platformUser]));
        platformUserDetailsByUserId.set(currentPlatformUser.userId, currentPlatformUser);

        const datasetsResult = await getDatasets(intellsysBaseUrl, accessToken, workspaceId);
        if (datasetsResult.success !== true) {
            throw datasetsResult.error;
        }
        const datasets = datasetsResult.data;

        if (isDhiraaguWorkspace(workspace) === true) {
            const dashboardConfig = getDhiraaguDashboardConfig({
                datasets,
                intellsysBaseUrl,
                accessToken,
                workspaceId,
                queryClient: context.queryClient,
                users,
                userTemplates,
                ouTemplates,
                timeZone,
                startTime: TS_START,
                endTime: TS_END,
            });

            return {
                dashboardConfig,
                datasets,
                defaultRange: {start: TS_START, end: TS_END},
                workspaceUser: context.currentWorkspaceUser,
                platformUser: currentPlatformUser,
            };
        } else if (isEmotoradWorkspace(workspace) === true) {
            const organizationalUnits = context.organizationalUnits;

            const dashboardConfig = getEmotoradDashboardConfig({
                datasets,
                intellsysBaseUrl,
                accessToken,
                workspaceId,
                queryClient: context.queryClient,
                users,
                userTemplates,
                platformUserDetailsByUserId,
                organizationalUnits,
                // OU templates power the OU UUID → display-name resolution that
                // `transformDataIfNeeded` runs inside `fetchAnalyticsData`.
                // Without them, OU dimension columns surface raw UUIDs in widgets like the heat map.
                ouTemplates,
                timeZone,
                startTime: TS_START,
                endTime: TS_END,
            });
            const canViewRoleReports =
                user !== null &&
                user !== undefined &&
                (user.templateId === EmotoradRoles.SuperAdmin as string || user.templateId === EmotoradRoles.SystemAdmin as string);
            const filteredDashboardRows = dashboardConfig.sections[0]!.rows.filter((widget) => {
                if (
                    widget.title === "KAM-wise Dealer Performance Report" ||
                    widget.title === "RM-wise Dealer Performance Report"
                ) {
                    return canViewRoleReports;
                }
                return true;
            });
            const newDaashboardConfig: DashboardConfig = {
                ...dashboardConfig,
                sections: [
                    {
                        id: dashboardConfig.sections[0]!.id,
                        title: dashboardConfig.sections[0]!.title,
                        rows: filteredDashboardRows,
                    },
                ],
            };
            return {
                dashboardConfig: newDaashboardConfig,
                datasets,
                defaultRange: {start: TS_START, end: TS_END},
                workspaceUser: context.currentWorkspaceUser,
                platformUser: currentPlatformUser,
            };
        } else if (isAmaronWorkspace(workspaceCapabilities) === true) {
            const resolvedAmaronDashboard = resolveDashboardDatasetsAndColumns(
                datasets,
                DASHBOARD_DATASET_KEYS_BY_WORKSPACE.amaron,
            );
            const eventContainerTemplate = getRequiredEventContainerTemplate(
                context.eventContainerTemplates,
                AmaronEventContainerTypes.Journeys as Uuid,
            );
            const eventContainerDispositions = getEventContainerDispositionOptions(
                createCapabilityUdoColumnResolver(context.workspace.id, context.userDefinedObjectTemplates),
                eventContainerTemplate,
                context.userDefinedObjectOptionsByTemplateId,
            );
            const sourceDataset = resolvedAmaronDashboard.datasets.amaronPerformanceDataset;
            const sourceDimensionId = resolvedAmaronDashboard.columnIds.amaronPerformanceDataset.utmSource;
            const sourceMetricId = resolvedAmaronDashboard.columnIds.amaronPerformanceDataset.totalLeads;
            const sourceValuesResult = await fetchAnalyticsData(
                intellsysBaseUrl,
                accessToken,
                workspaceId,
                context.queryClient,
                users,
                userTemplates,
                ouTemplates,
                resolvedAmaronDashboard,
                sourceDataset,
                {
                    datasetId: sourceDataset.id,
                    datasetType: DatasetType.nonTimeSeries,
                    dimensions: [{id: sourceDimensionId}],
                    metrics: [{id: sourceMetricId, aggregationFunction: MetricAggregationFunction.sum}],
                    filters: {type: FilterType.and, predicates: []},
                    sort: [],
                    from: TS_START,
                    to: TS_END,
                    timeZone,
                    queryVariant: AnalyticsQueryVariant.pointInTimeAsNonTimeSeries,
                },
                [...eventContainerDispositions],
            );
            const sourceColumn =
                sourceValuesResult.success === true
                    ? sourceValuesResult.data.columnMetadata.find(
                          (column) =>
                              column.id === sourceDimensionId || column.role === DatasetColumnRole.dimension,
                      )
                    : undefined;
            const amaronUtmSourceOptions =
                sourceValuesResult.success === true && sourceColumn !== undefined
                    ? Array.from(
                          new Set(
                              sourceValuesResult.data.dataPoints
                                  .map((row) => row[sourceColumn.columnIndex]?.value)
                                  .filter((value): value is string | number => value !== null && value !== undefined)
                                  .map(String)
                                  .map((value) => value.trim())
                                  .filter((value) => value.length > 0),
                          ),
                      )
                          .sort((left, right) => left.localeCompare(right))
                          .map((source) => ({label: source, value: source}))
                    : [];
            const dashboardConfig = getAmaronDashboardConfig({
                datasets,
                intellsysBaseUrl,
                accessToken,
                workspaceId,
                queryClient: context.queryClient,
                users,
                userTemplates,
                platformUserDetailsByUserId,
                ouTemplates,
                timeZone,
                startTime: TS_START,
                endTime: TS_END,
                eventContainerDispositions: [...eventContainerDispositions],
            });
            const dashboardConfigForUser: DashboardConfig =
                user.templateId === (AmaronRoles.Agent as string)
                    ? {
                          ...dashboardConfig,
                          sections: dashboardConfig.sections.map((section) => ({
                              ...section,
                              rows: section.rows.filter((row) => row.title !== "Agent-wise Performance"),
                          })),
                      }
                    : dashboardConfig;

            return {
                dashboardConfig: dashboardConfigForUser,
                datasets,
                defaultRange: {start: TS_START, end: TS_END},
                workspaceUser: context.currentWorkspaceUser,
                platformUser: currentPlatformUser,
                amaronUtmSourceOptions,
            };
        }

        throw new Error("Dashboard config not implemented for this workspace");
    },
    component: RouteComponent,
    errorComponent: ErrorComponent,
    pendingComponent: PendingComponent,
    notFoundComponent: NotFoundComponent,
});
